import React from 'react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface CompactToggleRowProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  tooltipText?: string;
  disabledTooltip?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export const CompactToggleRow: React.FC<CompactToggleRowProps> = ({
  icon,
  title,
  description,
  tooltipText,
  disabledTooltip,
  checked,
  onCheckedChange,
  disabled = false,
  className,
}) => {
  const row = (
    <div
      className={cn(
        "flex items-center justify-between p-2.5 rounded-lg transition-colors duration-150",
        "hover:bg-[#F8FAFC]",
        disabled && "opacity-50 pointer-events-none",
        className
      )}
    >
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <div className={cn(
          "w-4 h-4 flex-shrink-0 transition-colors duration-200",
          checked ? "text-[#1D4FFF]" : "text-[#9CA3AF]"
        )}>
          {icon}
        </div>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[12px] text-[#374151] font-medium">
              {title}
            </span>
            {tooltipText && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-[#9CA3AF] cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="text-xs z-[99999]"
                    style={{
                      width: '180px',
                      maxWidth: '180px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {tooltipText}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {description && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[10px] text-[#9CA3AF] leading-tight truncate cursor-default">
                    {description}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-[260px] text-xs z-[99999]"
                >
                  {description}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="data-[state=checked]:bg-[#1D4FFF] scale-90 flex-shrink-0 ml-2"
      />
    </div>
  );

  if (disabled && disabledTooltip) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-not-allowed">{row}</div>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="text-xs z-[99999]"
            style={{ maxWidth: '280px', whiteSpace: 'normal' }}
          >
            {disabledTooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return row;
};
